import { useEffect, useRef } from 'react';

export function OgImageGenerator() {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Log dimensions for verification
    if (cardRef.current) {
      console.log('OG Card dimensions:', cardRef.current.offsetWidth, 'x', cardRef.current.offsetHeight);
    }
  }, []);

  const handleDownloadInstructions = () => {
    alert(`Quick Checklist:
      
✅ Size: Exactly 1200 x 630 pixels
✅ Format: PNG (not JPEG)
✅ File size: Under 1MB
✅ Colors: Dark background with neon gradients
✅ Text: Clear and readable
✅ Filename: og-image.png
✅ Location: /public/ folder

The card above is already the correct size.
Use browser dev tools to capture a screenshot!`);
  };

  return (
    <div className="min-h-screen bg-black p-10 flex flex-col items-center">
      {/* OG Image Card - Exact dimensions 1200x630 */}
      <div 
        ref={cardRef}
        className="relative overflow-hidden shadow-2xl"
        style={{ width: '1200px', height: '630px' }}
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0118] via-[#1a0a2e] to-[#0a0118]" />
        
        {/* Animated gradient mesh */}
        <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] animate-float opacity-30">
          <div className="absolute top-[20%] left-[20%] w-[600px] h-[600px] bg-violet-500/20 rounded-full blur-[120px]" />
          <div className="absolute top-[70%] left-[80%] w-[700px] h-[700px] bg-fuchsia-500/20 rounded-full blur-[120px]" />
          <div className="absolute top-[50%] left-[50%] w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[100px]" />
        </div>

        {/* Glow orbs */}
        <div className="absolute -top-[100px] -right-[100px] w-[400px] h-[400px] bg-violet-500/30 rounded-full blur-[60px] pointer-events-none" />
        <div className="absolute -bottom-[150px] -left-[100px] w-[500px] h-[500px] bg-fuchsia-500/30 rounded-full blur-[60px] pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 h-full p-20 flex flex-col justify-between">
          {/* Logo */}
          <div 
            className="text-[48px] font-black tracking-[-2px]"
            style={{
              background: 'linear-gradient(135deg, #a78bfa 0%, #e879f9 50%, #22d3ee 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 30px rgba(167, 139, 250, 0.5))'
            }}
          >
            EO
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col justify-center gap-6">
            <h1 
              className="text-[92px] font-black leading-none tracking-[-3px] m-0"
              style={{
                background: 'linear-gradient(135deg, #fff 0%, #e0e7ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.1))'
              }}
            >
              Ergys Onuzi
            </h1>
            <p 
              className="text-[38px] font-semibold m-0 tracking-[-0.5px]"
              style={{
                background: 'linear-gradient(135deg, #a78bfa 0%, #e879f9 50%, #22d3ee 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Full-Stack Developer & Business Solutions Architect
            </p>
            <p className="text-[28px] text-white/70 m-0 font-normal">
              Building elegant digital experiences with modern tech
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center">
            <div className="text-[32px] font-bold text-white tracking-[-0.5px]">
              ergys.dev
            </div>
            <div className="bg-violet-500/20 border-2 border-violet-500/50 px-6 py-3 rounded-full text-[18px] text-violet-300 font-semibold backdrop-blur-sm">
              Portfolio 2026
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-10 max-w-4xl bg-[#0a0118] p-8 rounded-xl border border-violet-500/30">
        <h2 className="text-2xl font-bold text-violet-300 mb-4">📸 How to Save This as og-image.png</h2>
        
        <div className="mb-6">
          <p className="text-white/90 font-semibold mb-2">Quick Method (Screenshot):</p>
          <ol className="text-white/70 space-y-2 pl-5 list-decimal">
            <li>Right-click on the card above</li>
            <li>Select "Inspect Element" (or press F12)</li>
            <li>Find the <code className="bg-violet-500/20 px-2 py-1 rounded text-fuchsia-300 text-sm">{"<div ref={cardRef.current}>"}</code> element</li>
            <li>Right-click it in the inspector → "Capture node screenshot"</li>
            <li>Save as <code className="bg-violet-500/20 px-2 py-1 rounded text-fuchsia-300 text-sm">og-image.png</code> in your <code className="bg-violet-500/20 px-2 py-1 rounded text-fuchsia-300 text-sm">/public</code> folder</li>
          </ol>
        </div>

        <div className="mb-6">
          <p className="text-white/90 font-semibold mb-2">Professional Method (Recommended):</p>
          <ol className="text-white/70 space-y-2 pl-5 list-decimal">
            <li>Open Figma or Photoshop</li>
            <li>Set canvas to exactly <strong className="text-white">1200 x 630 pixels</strong></li>
            <li>Use the design above as reference</li>
            <li>Add your personal touch (optional)</li>
            <li>Export as PNG at 2x resolution</li>
            <li>Save as <code className="bg-violet-500/20 px-2 py-1 rounded text-fuchsia-300 text-sm">og-image.png</code> in <code className="bg-violet-500/20 px-2 py-1 rounded text-fuchsia-300 text-sm">/public</code></li>
          </ol>
        </div>

        <div className="mb-6">
          <p className="text-white/90 font-semibold mb-2">Canva Method (Easiest):</p>
          <ol className="text-white/70 space-y-2 pl-5 list-decimal">
            <li>Go to <a href="https://www.canva.com" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline">Canva.com</a></li>
            <li>Create custom size: 1200 x 630px</li>
            <li>Use dark background (#0a0118)</li>
            <li>Add your name, title, and website</li>
            <li>Apply gradient text effects (violet, fuchsia, cyan)</li>
            <li>Export as PNG</li>
          </ol>
        </div>

        <button
          onClick={handleDownloadInstructions}
          className="mt-4 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg hover:shadow-violet-500/40 transition-all hover:-translate-y-0.5 active:translate-y-0"
        >
          💡 Show Quick Tips
        </button>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(5deg); }
          66% { transform: translate(-20px, 20px) rotate(-5deg); }
        }
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}